import { ApiResponse } from "src/models";
import {
  CreateDistrictPayload,
  CreateSchoolPayload,
  DataPrivacyAgreementPayload,
  DpaUpload,
  OrganizationView,
  RejectedRow,
  RosterCheck,
  RosterCredentialsView,
  RosterScheduleResult,
  RosterSource,
  RosterSourcePayload,
  RosterStatus,
  RosterSyncRecord,
  RunRosterSyncResult,
  UpdateDistrictPayload,
} from "src/models/hierarchy";
import { DistrictHistoryArgs, HistoryEntry } from "src/models/history";
import { Page, toQueryString } from "src/models/staff";
import { LONG_REQUEST_TIMEOUT_MS, apiSlice } from "src/store/apiSlice";

const base = "/api/account/v1";

/** How often the Rostering tab refreshes the history and status while a sync runs (FR-031). */
export const ROSTER_POLL_MS = 5000;

/**
 * The polling interval for the sync history and status: {@link ROSTER_POLL_MS} while a sync is
 * running, otherwise 0 (no polling).
 */
export const rosterPollingInterval = (
  syncs: RosterSyncRecord[] | undefined,
  status?: RosterStatus | null,
) =>
  Boolean(status?.running) ||
  (syncs ?? []).some((sync) => sync.status === "running")
    ? ROSTER_POLL_MS
    : 0;

export const districtsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listDistricts: builder.query<OrganizationView[], void>({
      query: () => ({ url: `${base}/districts`, method: "GET" }),
      transformResponse: (r: ApiResponse<{ districts: OrganizationView[] }>) =>
        r.data.districts,
      providesTags: ["Districts"],
    }),
    getDistrict: builder.query<OrganizationView, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ district: OrganizationView }>) =>
        r.data.district,
      providesTags: (_r, _e, id) => [{ type: "Districts", id }],
    }),
    createDistrict: builder.mutation<OrganizationView, CreateDistrictPayload>({
      query: (body) => ({ url: `${base}/districts`, method: "POST", body }),
      transformResponse: (r: ApiResponse<{ district: OrganizationView }>) =>
        r.data.district,
      invalidatesTags: ["Districts"],
    }),
    updateDistrict: builder.mutation<
      OrganizationView,
      { districtId: string; body: UpdateDistrictPayload }
    >({
      query: ({ districtId, body }) => ({
        url: `${base}/districts/${districtId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (r: ApiResponse<{ district: OrganizationView }>) =>
        r.data.district,
      invalidatesTags: (_r, _e, { districtId }) => [
        "Districts",
        { type: "Districts", id: districtId },
        { type: "DistrictHistory", id: districtId },
      ],
    }),
    listSchools: builder.query<OrganizationView[], string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/schools`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ schools: OrganizationView[] }>) =>
        r.data.schools,
      providesTags: (_r, _e, districtId) => [
        { type: "Schools", id: districtId },
      ],
    }),
    createSchool: builder.mutation<
      OrganizationView,
      { districtId: string; body: CreateSchoolPayload }
    >({
      query: ({ districtId, body }) => ({
        url: `${base}/districts/${districtId}/schools`,
        method: "POST",
        body,
      }),
      transformResponse: (r: ApiResponse<{ school: OrganizationView }>) =>
        r.data.school,
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "Schools", id: districtId },
      ],
    }),
    getSchool: builder.query<OrganizationView, string>({
      query: (schoolId) => ({
        url: `${base}/schools/${schoolId}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ school: OrganizationView }>) =>
        r.data.school,
      providesTags: (_r, _e, id) => [{ type: "Schools", id }],
    }),
    updateSchool: builder.mutation<
      OrganizationView,
      {
        schoolId: string;
        body: {
          name?: string;
          /** IANA time zone, or null to follow the district's. */
          timezone?: string | null;
          externalId?: string | null;
        };
      }
    >({
      query: ({ schoolId, body }) => ({
        url: `${base}/schools/${schoolId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (r: ApiResponse<{ school: OrganizationView }>) =>
        r.data.school,
      invalidatesTags: (r) => [
        "Schools",
        ...(r?.parentId ? [{ type: "Schools" as const, id: r.parentId }] : []),
      ],
    }),
    /**
     * Records the district's MFA attestation (R20). `provider` must equal the district's
     * `ssoProvider` (409 `PROVIDER_MISMATCH`); the server records who attested and when.
     */
    setMfaAttestation: builder.mutation<
      void,
      { districtId: string; provider: string }
    >({
      query: ({ districtId, provider }) => ({
        url: `${base}/districts/${districtId}/mfa-attestation`,
        method: "PUT",
        body: { provider },
      }),
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "Districts", id: districtId },
        { type: "DistrictHistory", id: districtId },
      ],
    }),
    /** Withdraws the MFA attestation (`DELETE …/mfa-attestation`, R20). Audited. */
    deleteMfaAttestation: builder.mutation<void, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `${base}/districts/${districtId}/mfa-attestation`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "Districts", id: districtId },
        { type: "DistrictHistory", id: districtId },
      ],
    }),
    /** Records or replaces the data privacy agreement (`PUT …/data-privacy-agreement`, R20). */
    putDataPrivacyAgreement: builder.mutation<
      void,
      { districtId: string } & DataPrivacyAgreementPayload
    >({
      query: ({ districtId, ...body }) => ({
        url: `${base}/districts/${districtId}/data-privacy-agreement`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "Districts", id: districtId },
        { type: "DistrictHistory", id: districtId },
      ],
    }),
    /** A presigned PUT for the agreement PDF (10 MB or less); its `uploadId` goes in the PUT above. */
    createDpaUpload: builder.mutation<DpaUpload, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `${base}/districts/${districtId}/data-privacy-agreement/upload`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<DpaUpload>) => r.data,
    }),
    /**
     * A 15-minute presigned GET for the agreement PDF. Never cached: each download asks for a fresh
     * link (use the lazy hook).
     */
    getDpaDocumentUrl: builder.query<{ url: string }, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/data-privacy-agreement/document`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ url: string }>) => r.data,
      keepUnusedDataFor: 0,
    }),
    /**
     * Sync history, newest first, without rejected rows (`getSyncRejected` pages those). The page
     * polls it every {@link ROSTER_POLL_MS} only while a sync is running ({@link rosterPollingInterval}).
     */
    listRosterSyncs: builder.query<RosterSyncRecord[], string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/rostering/syncs?limit=50`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ syncs: RosterSyncRecord[] }>) =>
        r.data.syncs,
      providesTags: (_r, _e, districtId) => [
        { type: "RosterSyncs", id: districtId },
      ],
    }),
    /** Connection, schedule and running-sync state (`GET …/rostering/status`). */
    getRosterStatus: builder.query<RosterStatus, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/rostering/status`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<RosterStatus>) => r.data,
      providesTags: (_r, _e, districtId) => [
        { type: "RosterStatus", id: districtId },
      ],
    }),
    /** Who saved the vendor credentials and when; never the secret (R5). */
    getRosterCredentials: builder.query<RosterCredentialsView, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/rostering/credentials`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<RosterCredentialsView>) => r.data,
      providesTags: (_r, _e, districtId) => [
        { type: "RosterCredentials", id: districtId },
      ],
    }),
    /** Saves the vendor credentials. Write-only: the response never carries the secret. */
    putRosterCredentials: builder.mutation<
      RosterCredentialsView,
      { districtId: string; clientId: string; clientSecret: string }
    >({
      query: ({ districtId, clientId, clientSecret }) => ({
        url: `${base}/districts/${districtId}/rostering/credentials`,
        method: "PUT",
        body: { clientId, clientSecret },
      }),
      transformResponse: (r: ApiResponse<RosterCredentialsView>) => r.data,
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "RosterCredentials", id: districtId },
        { type: "RosterStatus", id: districtId },
      ],
    }),
    /** Tests the saved vendor connection within 15 s and records it as `lastCheck` (R26). */
    testRosterSource: builder.mutation<RosterCheck, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/rostering/source/test`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<RosterCheck>) => r.data,
      invalidatesTags: (_r, _e, districtId) => [
        { type: "RosterStatus", id: districtId },
        { type: "Districts", id: districtId },
      ],
    }),
    /** Pauses or resumes scheduled syncs; returns the next run the server calculated (R3). */
    putRosterSchedule: builder.mutation<
      RosterScheduleResult,
      { districtId: string; paused: boolean }
    >({
      query: ({ districtId, paused }) => ({
        url: `${base}/districts/${districtId}/rostering/schedule`,
        method: "PUT",
        body: { paused },
      }),
      transformResponse: (r: ApiResponse<RosterScheduleResult>) => r.data,
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "RosterStatus", id: districtId },
        { type: "Districts", id: districtId },
      ],
    }),
    /** One page of a sync's rejected rows; at most 500 are kept per sync (R9). */
    getSyncRejected: builder.query<
      Page<RejectedRow>,
      { districtId: string; syncId: string; limit?: number; offset?: number }
    >({
      query: ({ districtId, syncId, limit, offset }) => ({
        url: `${base}/districts/${districtId}/rostering/syncs/${syncId}/rejected${toQueryString(
          { limit, offset },
        )}`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{ items: RejectedRow[]; total: number }>,
        _meta,
        { limit, offset },
      ) => ({
        items: r.data.items,
        total: r.data.total,
        limit: limit ?? r.data.items.length,
        offset: offset ?? 0,
      }),
      providesTags: (_r, _e, { syncId }) => [
        { type: "RosterRejected", id: syncId },
      ],
    }),
    importRoster: builder.mutation<
      RosterSyncRecord,
      {
        districtId: string;
        file: File;
        format: "oneroster-csv" | "simple-roster";
        dryRun: boolean;
      }
    >({
      query: ({ districtId, file, format, dryRun }) => {
        const body = new FormData();
        body.append("file", file);
        body.append("format", format);
        body.append("dryRun", String(dryRun));
        return {
          url: `${base}/districts/${districtId}/rostering/import`,
          method: "POST",
          body,
          // The server parses and applies the whole file inside the request.
          timeout: LONG_REQUEST_TIMEOUT_MS,
        };
      },
      transformResponse: (r: ApiResponse<{ sync: RosterSyncRecord }>) =>
        r.data.sync,
      // An import records `rosterSource.lastImport` (R9), so the district and the list change too.
      invalidatesTags: (_r, _e, { districtId, dryRun }) =>
        dryRun
          ? [{ type: "RosterSyncs", id: districtId }]
          : [
              "Districts",
              { type: "Districts", id: districtId },
              { type: "RosterSyncs", id: districtId },
              { type: "RosterStatus", id: districtId },
              { type: "Schools", id: districtId },
              "Classes",
              "Students",
            ],
    }),
    /** Saves the vendor connection; `provider: null` disconnects (credentials cleared, students kept). */
    setRosterSource: builder.mutation<
      RosterSource | null,
      { districtId: string } & RosterSourcePayload
    >({
      query: ({ districtId, ...body }) => ({
        url: `${base}/districts/${districtId}/rostering/source`,
        method: "PUT",
        body,
      }),
      transformResponse: (
        r: ApiResponse<{ rosterSource: RosterSource | null }>,
      ) => r.data.rosterSource,
      invalidatesTags: (_r, _e, { districtId }) => [
        "Districts",
        { type: "Districts", id: districtId },
        { type: "RosterStatus", id: districtId },
        { type: "RosterCredentials", id: districtId },
      ],
    }),
    /**
     * Queues a vendor sync (202 `{ syncId, jobId }`, 409 `SYNC_RUNNING` while one runs). A preview
     * (`dryRun`) still runs inline and returns its result as `sync`.
     */
    runRosterSync: builder.mutation<
      RunRosterSyncResult,
      { districtId: string; dryRun: boolean }
    >({
      query: ({ districtId, dryRun }) => ({
        url: `${base}/districts/${districtId}/rostering/syncs`,
        method: "POST",
        body: { dryRun },
        // A preview reads the vendor inside the request.
        ...(dryRun ? { timeout: LONG_REQUEST_TIMEOUT_MS } : {}),
      }),
      transformResponse: (r: ApiResponse<RunRosterSyncResult>) => r.data,
      // The sync itself runs in the background; the history and status poll until it ends.
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "RosterSyncs", id: districtId },
        { type: "RosterStatus", id: districtId },
      ],
    }),
    /**
     * District, license, MFA, agreement, privacy and clean-up history, newest first
     * (`GET v1/districts/:id/history`, R21). Pages fit `HistoryTimeline`'s `usePage`.
     */
    getDistrictHistory: builder.query<Page<HistoryEntry>, DistrictHistoryArgs>({
      query: ({ districtId, category, limit, offset }) => ({
        url: `${base}/districts/${districtId}/history${toQueryString({
          category: Array.isArray(category) ? category.join(",") : category,
          limit,
          offset,
        })}`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{ items: HistoryEntry[]; total: number }>,
        _meta,
        { limit, offset },
      ) => ({
        items: r.data.items,
        total: r.data.total,
        limit: limit ?? r.data.items.length,
        offset: offset ?? 0,
      }),
      providesTags: (_r, _e, { districtId }) => [
        { type: "DistrictHistory", id: districtId },
      ],
    }),
  }),
});

export const {
  useListDistrictsQuery,
  useGetDistrictQuery,
  useCreateDistrictMutation,
  useUpdateDistrictMutation,
  useListSchoolsQuery,
  useCreateSchoolMutation,
  useGetSchoolQuery,
  useUpdateSchoolMutation,
  useSetMfaAttestationMutation,
  useDeleteMfaAttestationMutation,
  usePutDataPrivacyAgreementMutation,
  useCreateDpaUploadMutation,
  useLazyGetDpaDocumentUrlQuery,
  useListRosterSyncsQuery,
  useGetRosterStatusQuery,
  useGetRosterCredentialsQuery,
  usePutRosterCredentialsMutation,
  useTestRosterSourceMutation,
  usePutRosterScheduleMutation,
  useGetSyncRejectedQuery,
  useImportRosterMutation,
  useSetRosterSourceMutation,
  useRunRosterSyncMutation,
  useGetDistrictHistoryQuery,
} = districtsApiSlice;
